import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, Controller, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import {
  Risk,
  CreateRiskData,
  UpdateRiskData,
  RiskLikelihood,
  RiskImpact,
  RiskStatus,
  TreatmentPlan,
  calculateRiskScore,
  getRiskLevel,
  getLikelihoodValue,
  getImpactValue,
  numberToLikelihood,
  numberToImpact,
} from '../../types/risk.types';
import { User } from '../../types/auth.types';
import riskService from '../../services/risk.service';
import { useCompanyOptions, useDepartmentOptions, useTeamOptions } from '../../hooks/useOrgQueries';
import { useCreateRisk, useUpdateRisk, useUserOptions } from '../../hooks/useRegisterQueries';
import {
  LedgerCombobox,
  LedgerDialog,
  LedgerField,
  LedgerInput,
  LedgerSelect,
  LedgerSelectOption,
  LedgerWizardStep,
  useLedgerWizard,
} from '../ui';
import { cn } from '../../lib/cn';

// Optional fields carry .defined().default(...) so the schema's inferred type
// lines up with what yupResolver hands back — a bare .string() infers as an
// optional property on one side but required-with-undefined on the other.
const schema = yup.object({
  title: yup.string().required('Title is required').default(''),
  description: yup.string().required('Description is required').default(''),
  category: yup.string().required('Category is required').default('Operational'),
  status: yup.string().required('Status is required').default('DRAFT'),
  inherent_likelihood: yup.string().required('Inherent likelihood is required').default('MEDIUM'),
  inherent_impact: yup.string().required('Inherent impact is required').default('MEDIUM'),
  inherent_notes: yup.string().defined().default(''),
  residual_likelihood: yup.string().required('Residual likelihood is required').default('MEDIUM'),
  residual_impact: yup.string().required('Residual impact is required').default('MEDIUM'),
  residual_notes: yup.string().defined().default(''),
  treatment_plan: yup.string().required('Treatment plan is required').default('MITIGATE'),
  treatment_details: yup.string().defined().default(''),
  treatment_owner: yup.string().defined().default(''),
  treatment_due_date: yup.string().defined().default(''),
  owner: yup.string().required('Risk owner is required').default(''),
  vendor_note: yup.string().defined().default(''),
  organizationLevel: yup
    .string()
    .required('Organization level is required')
    .test('organization-validation', function (value) {
      const { company, team, department } = this.parent;

      if (value === 'company') {
        if (!company) return this.createError({ message: 'Please select a company' });
        if (team || department)
          return this.createError({ message: 'Only company should be selected for company level' });
        return true;
      } else if (value === 'team') {
        if (!company) return this.createError({ message: 'Please select a company first' });
        if (!team) return this.createError({ message: 'Please select a team' });
        if (department)
          return this.createError({ message: 'Department should not be selected for team level' });
        return true;
      } else if (value === 'department') {
        if (!company) return this.createError({ message: 'Please select a company first' });
        if (!team) return this.createError({ message: 'Please select a team first' });
        if (!department) return this.createError({ message: 'Please select a department' });
        return true;
      }

      return this.createError({ message: 'Please select an organization level' });
    })
    .default(''),
  company: yup.string().defined().default(''),
  team: yup.string().defined().default(''),
  department: yup.string().defined().default(''),
  next_review_date: yup.string().defined().default(''),
  last_reviewed_date: yup.string().defined().default(''),
  tags: yup.array().of(yup.string().defined()).defined().default(() => [] as string[]),
});

type RiskFormValues = yup.InferType<typeof schema>;

const defaultValues: RiskFormValues = {
  title: '',
  description: '',
  category: 'Operational',
  status: 'DRAFT',
  inherent_likelihood: 'MEDIUM',
  inherent_impact: 'MEDIUM',
  inherent_notes: '',
  residual_likelihood: 'MEDIUM',
  residual_impact: 'MEDIUM',
  residual_notes: '',
  treatment_plan: 'MITIGATE',
  treatment_details: '',
  treatment_owner: '',
  treatment_due_date: '',
  owner: '',
  vendor_note: '',
  organizationLevel: '',
  company: '',
  team: '',
  department: '',
  next_review_date: '',
  last_reviewed_date: '',
  tags: [],
};

// category is a free-text CharField with no choices, so the register holds
// values like Cybersecurity and Third-Party / Vendor that no fixed list
// covers. These are the fallback suggestions for an empty register; whatever
// is actually in use gets merged in from the API.
const DEFAULT_CATEGORIES = [
  'Technical',
  'Operational',
  'Financial',
  'Compliance',
  'Strategic',
  'Reputational',
  'Other',
];

const statusOptions: LedgerSelectOption[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'CLOSED', label: 'Closed' },
];

const ratingOptions: LedgerSelectOption[] = [
  { value: 'VERY_LOW', label: 'Very Low (1)' },
  { value: 'LOW', label: 'Low (2)' },
  { value: 'MEDIUM', label: 'Medium (3)' },
  { value: 'HIGH', label: 'High (4)' },
  { value: 'VERY_HIGH', label: 'Very High (5)' },
];

const treatmentPlanOptions: LedgerSelectOption[] = [
  { value: 'MITIGATE', label: 'Mitigate' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'AVOID', label: 'Avoid' },
  { value: 'ACCEPT', label: 'Accept' },
];

const levelOptions: LedgerSelectOption[] = [
  { value: '', label: 'Select a level' },
  { value: 'company', label: 'Company Level' },
  { value: 'team', label: 'Team Level' },
  { value: 'department', label: 'Department Level' },
];

const userLabel = (user: User): string =>
  `${user.email}${user.firstName || user.lastName ? ` (${[user.firstName, user.lastName].filter(Boolean).join(' ')})` : ''}`;

// Backend date fields arrive as date-only strings; trim a datetime shape too
// without round-tripping through Date (which would shift across timezones).
const toDateInput = (value?: string | null): string => (value ? value.split('T')[0] : '');

// The API stores exactly one org FK per risk, so the level is whichever key
// is present, and the parent ids arrive empty (the chain-backfill effect
// below fills them from the option lists once those load).
const valuesFromRisk = (risk: Risk): RiskFormValues => {
  let organizationLevel = '';
  let company = '';
  let team = '';
  let department = '';
  if (risk.department) {
    organizationLevel = 'department';
    department = risk.department;
  } else if (risk.team) {
    organizationLevel = 'team';
    team = risk.team;
  } else if (risk.company) {
    organizationLevel = 'company';
    company = risk.company;
  }
  return {
    title: risk.title || '',
    description: risk.description || '',
    category: risk.category || 'Other',
    status: risk.status || 'ACTIVE',
    inherent_likelihood: risk.inherent_likelihood ? numberToLikelihood(risk.inherent_likelihood) : 'MEDIUM',
    inherent_impact: risk.inherent_impact ? numberToImpact(risk.inherent_impact) : 'MEDIUM',
    inherent_notes: risk.inherent_notes || '',
    residual_likelihood: risk.residual_likelihood ? numberToLikelihood(risk.residual_likelihood) : 'MEDIUM',
    residual_impact: risk.residual_impact ? numberToImpact(risk.residual_impact) : 'MEDIUM',
    residual_notes: risk.residual_notes || '',
    treatment_plan: risk.treatment_plan || 'MITIGATE',
    treatment_details: risk.treatment_details || '',
    treatment_owner: risk.treatment_owner || '',
    treatment_due_date: toDateInput(risk.treatment_due_date),
    owner: risk.owner || '',
    vendor_note: risk.vendor_note || '',
    organizationLevel,
    company,
    team,
    department,
    next_review_date: toDateInput(risk.next_review_date),
    last_reviewed_date: toDateInput(risk.last_reviewed_date),
    tags: risk.tags || [],
  };
};

// Ports the previous create assembly exactly: display enums map to the
// backend's 1-5 integers, blank optionals drop to undefined, vendor_note
// stays an empty string, and exactly one org key rides along per the
// selected level. Dates are already YYYY-MM-DD strings — the service layer
// trims them to the same wire format the old Date.toISOString() produced.
const toRiskPayload = (values: RiskFormValues): CreateRiskData => ({
  title: values.title,
  description: values.description,
  category: values.category,
  inherent_likelihood: getLikelihoodValue(values.inherent_likelihood as RiskLikelihood),
  inherent_impact: getImpactValue(values.inherent_impact as RiskImpact),
  inherent_notes: values.inherent_notes || undefined,
  residual_likelihood: getLikelihoodValue(values.residual_likelihood as RiskLikelihood),
  residual_impact: getImpactValue(values.residual_impact as RiskImpact),
  residual_notes: values.residual_notes || undefined,
  treatment_plan: values.treatment_plan as TreatmentPlan,
  treatment_details: values.treatment_details || undefined,
  treatment_owner: values.treatment_owner || undefined,
  treatment_due_date: values.treatment_due_date || undefined,
  status: values.status as RiskStatus,
  owner: values.owner,
  vendor_note: values.vendor_note || '',
  // Only send the organization field matching the level — the backend
  // expects exactly one to be set.
  company: values.organizationLevel === 'company' ? values.company || undefined : undefined,
  team: values.organizationLevel === 'team' ? values.team || undefined : undefined,
  department: values.organizationLevel === 'department' ? values.department || undefined : undefined,
  next_review_date: values.next_review_date || undefined,
  last_reviewed_date: values.last_reviewed_date || undefined,
  tags: values.tags,
});

// The update assembly mirrors the previous edit path: organization keys are
// included only when the assignment actually changed (the backend preserves
// the existing binding when none are sent), and undefined/null entries are
// stripped so the PATCH stays minimal.
const toRiskUpdatePayload = (values: RiskFormValues, original: Risk): UpdateRiskData => {
  const { company, team, department, ...base } = toRiskPayload(values);
  void company;
  void team;
  void department;
  const data: UpdateRiskData = { ...base };

  const orgChanged =
    values.organizationLevel !== original.organizationLevel ||
    values.company !== original.company ||
    values.team !== original.team ||
    values.department !== original.department;
  if (orgChanged) {
    if (values.organizationLevel === 'company') {
      data.company = values.company || original.company;
    } else if (values.organizationLevel === 'team') {
      data.team = values.team || original.team;
    } else if (values.organizationLevel === 'department') {
      data.department = values.department || original.department;
    }
  }

  (Object.keys(data) as Array<keyof UpdateRiskData>).forEach((key) => {
    if (data[key] === undefined || data[key] === null) {
      delete data[key];
    }
  });
  return data;
};

const textareaClass =
  'w-full border border-rule-faint bg-transparent p-2 text-scale-1 text-ink outline-none focus:border-accent';

const sectionRuleClass = 'cap-label mb-3 border-b border-ink pb-0.5 sm:col-span-2';

interface TagsFieldProps {
  value: string[];
  onChange: (next: string[]) => void;
}

// Enter commits the draft as a tag chip (matching the old freeSolo behavior);
// preventDefault keeps that Enter from submitting the surrounding form.
const TagsField: React.FC<TagsFieldProps> = ({ value, onChange }) => {
  const [draft, setDraft] = useState('');
  return (
    <div>
      {value.length > 0 && (
        <ul className="mb-1 flex flex-wrap gap-1">
          {value.map((tag) => (
            <li
              key={tag}
              className="flex items-center gap-1 border border-ink px-2 py-0.5 font-mono text-scale-0 text-ink"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                onClick={() => onChange(value.filter((existing) => existing !== tag))}
                className="cursor-pointer text-ink-muted hover:text-ink"
              >
                &#x2715;
              </button>
            </li>
          ))}
        </ul>
      )}
      <LedgerInput
        id="risk-tags"
        value={draft}
        placeholder="Add tags..."
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            const tag = draft.trim();
            if (tag && !value.includes(tag)) onChange([...value, tag]);
            setDraft('');
          }
        }}
      />
    </div>
  );
};

interface RiskFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  risk?: Risk | null;
}

const RiskFormDialogInner: React.FC<RiskFormDialogProps> = ({ open, onClose, onSuccess, risk }) => {
  const [error, setError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  const createRisk = useCreateRisk();
  const updateRisk = useUpdateRisk();
  const saving = createRisk.isPending || updateRisk.isPending;

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    watch,
    formState: { errors, isSubmitting },
  } = useFormContext<RiskFormValues>();

  // The exactly-one-level rule lives on organizationLevel and reads company,
  // team and department off the parent. react-hook-form revalidates the field
  // that changed and nothing else, so choosing a company left "Please select a
  // company" on screen underneath the company you had just chosen. Refresh the
  // field that owns the message when a field its rule depends on moves.
  //
  // Only when it is already showing something. Validating an untouched chain
  // would put the next requirement on screen before the user has had a chance
  // to meet it, which is the opposite complaint.
  const refreshOrgMessage = () => {
    if (errors.organizationLevel) void trigger('organizationLevel');
  };

  const organizationLevel = watch('organizationLevel');
  const companyId = watch('company');
  const teamId = watch('team');
  const departmentId = watch('department');

  // Owners must be able to see the risk, which the backend scopes to the
  // risk's company (see user_can_access_org_scope). Scope the picker the same
  // way so it never offers an owner the API would reject; null keeps it
  // disabled until an organization is chosen.
  const ownerScope = open ? companyId || null : null;
  const usersQuery = useUserOptions(ownerScope);
  const companiesQuery = useCompanyOptions(open);
  const teamsQuery = useTeamOptions(open);
  const departmentsQuery = useDepartmentOptions(open);
  const vendorNotesQuery = useQuery({
    queryKey: ['vendor-notes'],
    queryFn: () => riskService.getVendorNotes(),
    enabled: open,
    staleTime: 300_000,
  });
  const categoriesQuery = useQuery({
    queryKey: ['risk-categories'],
    queryFn: () => riskService.getCategories(),
    enabled: open,
    staleTime: 300_000,
  });
  // The register's own categories first, since those are what a user is most
  // likely to reuse, then the defaults for anything not represented yet.
  const categorySuggestions = useMemo(() => {
    const seen = new Set<string>();
    return [...(categoriesQuery.data ?? []), ...DEFAULT_CATEGORIES].filter((category) => {
      const key = category.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [categoriesQuery.data]);
  const orgOptionsError = companiesQuery.isError || teamsQuery.isError || departmentsQuery.isError;
  const [inherentLikelihood, inherentImpact, residualLikelihood, residualImpact] = watch([
    'inherent_likelihood',
    'inherent_impact',
    'residual_likelihood',
    'residual_impact',
  ]);

  // A risk bound at team or department level arrives without its parent ids
  // (the API stores exactly one org FK). Fill the chain from the option
  // lists once they load so the cascade selects display it and the
  // exactly-one-level validation sees a complete chain.
  useEffect(() => {
    if (!open || !departmentId || teamId) return;
    const department = (departmentsQuery.data ?? []).find((item) => item.id === departmentId);
    if (department) setValue('team', department.team);
  }, [open, departmentId, teamId, departmentsQuery.data, setValue]);

  useEffect(() => {
    if (!open || !teamId || companyId) return;
    const team = (teamsQuery.data ?? []).find((item) => item.id === teamId);
    if (team) setValue('company', team.company);
  }, [open, teamId, companyId, teamsQuery.data, setValue]);

  // A company switch can strand the current owner/treatment_owner outside the
  // new org. Once the scoped list has loaded, drop any selection that is no
  // longer assignable so the form never submits an owner the API rejects.
  const ownerFieldValue = watch('owner');
  const treatmentOwnerFieldValue = watch('treatment_owner');
  const scopedUserIds = (usersQuery.data ?? []).map((user) => user.id).join(',');
  useEffect(() => {
    if (!open || usersQuery.isPending || usersQuery.isFetching) return;
    const ids = new Set(scopedUserIds ? scopedUserIds.split(',') : []);
    if (ownerFieldValue && !ids.has(ownerFieldValue)) setValue('owner', '');
    if (treatmentOwnerFieldValue && !ids.has(treatmentOwnerFieldValue)) setValue('treatment_owner', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopedUserIds, usersQuery.isPending, usersQuery.isFetching, ownerFieldValue, treatmentOwnerFieldValue]);

  const handleClose = () => {
    reset(defaultValues);
    setError(null);
    onClose();
  };

  const userOptions: LedgerSelectOption[] = (usersQuery.data ?? []).map((user) => ({
    value: user.id,
    label: userLabel(user),
  }));
  // Until a company is chosen the owner set is unknown, so prompt for the org
  // instead of showing an empty picker.
  const ownerPlaceholder = ownerScope ? 'Select an owner' : 'Select an organization first';
  const ownerOptions: LedgerSelectOption[] = [{ value: '', label: ownerPlaceholder }, ...userOptions];
  const treatmentOwnerOptions: LedgerSelectOption[] = [
    { value: '', label: ownerScope ? 'None' : 'Select an organization first' },
    ...userOptions,
  ];

  const companies = companiesQuery.data ?? [];
  const teams = (teamsQuery.data ?? []).filter((team) => team.company === companyId);
  const departments = (departmentsQuery.data ?? []).filter((department) => department.team === teamId);

  const companyOptions: LedgerSelectOption[] = [
    { value: '', label: 'Select a company' },
    ...companies.map((company) => ({ value: company.id, label: company.name })),
  ];
  const teamOptions: LedgerSelectOption[] = [
    {
      value: '',
      label: companyId ? (teams.length ? 'Select a team' : 'No teams found') : 'Select a company first',
    },
    ...teams.map((team) => ({ value: team.id, label: team.name })),
  ];
  const departmentOptions: LedgerSelectOption[] = [
    {
      value: '',
      label: teamId
        ? departments.length
          ? 'Select a department'
          : 'No departments found'
        : 'Select a team first',
    },
    ...departments.map((department) => ({ value: department.id, label: department.name })),
  ];

  const inherentScore = calculateRiskScore(
    inherentLikelihood as RiskLikelihood,
    inherentImpact as RiskImpact,
  );
  const residualScore = calculateRiskScore(
    residualLikelihood as RiskLikelihood,
    residualImpact as RiskImpact,
  );

  const titleField = register('title');

  const steps: LedgerWizardStep<RiskFormValues>[] = [
    {
      key: 'details',
      label: 'Details',
      fields: ['title', 'description', 'category', 'status'],
      content: (
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <LedgerField
            label="Risk Title"
            htmlFor="risk-title"
            error={errors.title?.message}
            className="sm:col-span-2"
          >
            <LedgerInput
              id="risk-title"
              required
              invalid={!!errors.title}
              {...titleField}
              ref={(element) => {
                titleField.ref(element);
                titleInputRef.current = element;
              }}
            />
          </LedgerField>

          <LedgerField
            label="Description"
            htmlFor="risk-description"
            error={errors.description?.message}
            className="sm:col-span-2"
          >
            <textarea
              id="risk-description"
              rows={3}
              required
              aria-invalid={errors.description ? true : undefined}
              className={cn(textareaClass, errors.description ? 'border-sev-critical' : undefined)}
              {...register('description')}
            />
          </LedgerField>

          <LedgerField
            label="Category"
            htmlFor="risk-category"
            error={errors.category?.message}
            hint="Pick a suggestion or type your own"
          >
            <LedgerInput
              id="risk-category"
              list="risk-categories"
              aria-label="Category"
              aria-invalid={errors.category ? true : undefined}
              {...register('category')}
            />
            <datalist id="risk-categories">
              {categorySuggestions.map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>
          </LedgerField>

          <LedgerField label="Status" error={errors.status?.message}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Status"
                  value={field.value}
                  options={statusOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>
        </div>
      ),
    },
    {
      key: 'assessment',
      label: 'Assessment',
      fields: [
        'inherent_likelihood',
        'inherent_impact',
        'inherent_notes',
        'residual_likelihood',
        'residual_impact',
        'residual_notes',
      ],
      content: (
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <div className={sectionRuleClass}>Inherent Risk Assessment (Before Treatment)</div>

          <LedgerField label="Inherent Likelihood" error={errors.inherent_likelihood?.message}>
            <Controller
              name="inherent_likelihood"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Inherent Likelihood"
                  value={field.value}
                  options={ratingOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Inherent Impact" error={errors.inherent_impact?.message}>
            <Controller
              name="inherent_impact"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Inherent Impact"
                  value={field.value}
                  options={ratingOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Inherent Risk Notes" htmlFor="risk-inherent-notes" className="sm:col-span-2">
            <textarea
              id="risk-inherent-notes"
              rows={2}
              className={textareaClass}
              {...register('inherent_notes')}
            />
          </LedgerField>

          <p className="mb-4 font-mono text-scale-1 text-ink sm:col-span-2">
            Inherent Risk Score: {inherentScore} ({getRiskLevel(inherentScore)})
          </p>

          <div className={sectionRuleClass}>Residual Risk Assessment (After Treatment)</div>

          <LedgerField label="Residual Likelihood" error={errors.residual_likelihood?.message}>
            <Controller
              name="residual_likelihood"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Residual Likelihood"
                  value={field.value}
                  options={ratingOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Residual Impact" error={errors.residual_impact?.message}>
            <Controller
              name="residual_impact"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Residual Impact"
                  value={field.value}
                  options={ratingOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Residual Risk Notes" htmlFor="risk-residual-notes" className="sm:col-span-2">
            <textarea
              id="risk-residual-notes"
              rows={2}
              className={textareaClass}
              {...register('residual_notes')}
            />
          </LedgerField>

          <p className="font-mono text-scale-1 text-ink sm:col-span-2">
            Residual Risk Score: {residualScore} ({getRiskLevel(residualScore)})
            {inherentScore > residualScore && (
              <span className="text-ink-muted">
                {' '}
                · Risk Reduction: {Math.round(((inherentScore - residualScore) / inherentScore) * 100)}%
              </span>
            )}
          </p>
        </div>
      ),
    },
    {
      key: 'assignment',
      label: 'Assignment',
      fields: [
        'treatment_plan',
        'treatment_details',
        'treatment_owner',
        'treatment_due_date',
        'owner',
        'vendor_note',
        'organizationLevel',
        'company',
        'team',
        'department',
        'next_review_date',
        'last_reviewed_date',
        'tags',
      ],
      content: (
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <div className={sectionRuleClass}>Treatment Plan</div>

          <LedgerField label="Treatment Plan" error={errors.treatment_plan?.message}>
            <Controller
              name="treatment_plan"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Treatment Plan"
                  value={field.value}
                  options={treatmentPlanOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Treatment Details" htmlFor="risk-treatment-details" className="sm:col-span-2">
            <textarea
              id="risk-treatment-details"
              rows={3}
              className={textareaClass}
              {...register('treatment_details')}
            />
          </LedgerField>

          <LedgerField label="Treatment Due Date" htmlFor="risk-treatment-due-date">
            <LedgerInput id="risk-treatment-due-date" type="date" {...register('treatment_due_date')} />
          </LedgerField>

          <div className={sectionRuleClass}>Ownership &amp; Organization</div>

          <LedgerField label="Organization Level" error={errors.organizationLevel?.message}>
            <Controller
              name="organizationLevel"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Organization Level"
                  value={field.value}
                  options={levelOptions}
                  onValueChange={(next) => {
                    // Only reset the fields that are not part of the new
                    // level: team keeps its parent company, department keeps
                    // both.
                    if (next === 'company') {
                      setValue('team', '');
                      setValue('department', '');
                    } else if (next === 'team') {
                      setValue('department', '');
                    }
                    field.onChange(next);
                  }}
                />
              )}
            />
          </LedgerField>

          {organizationLevel !== '' && (
            <LedgerField label="Company">
              <Controller
                name="company"
                control={control}
                render={({ field }) => (
                  <LedgerSelect
                    ariaLabel="Company"
                    value={field.value}
                    options={companyOptions}
                    onValueChange={(next) => {
                      // A company switch orphans the previous team and
                      // department selections.
                      if (next !== field.value) {
                        setValue('team', '');
                        setValue('department', '');
                      }
                      field.onChange(next);
                      refreshOrgMessage();
                    }}
                  />
                )}
              />
            </LedgerField>
          )}

          {(organizationLevel === 'team' || organizationLevel === 'department') && (
            <LedgerField label="Team">
              <Controller
                name="team"
                control={control}
                render={({ field }) => (
                  <LedgerSelect
                    ariaLabel="Team"
                    value={field.value}
                    options={teamOptions}
                    disabled={!companyId}
                    onValueChange={(next) => {
                      if (next !== field.value) setValue('department', '');
                      field.onChange(next);
                      refreshOrgMessage();
                    }}
                  />
                )}
              />
            </LedgerField>
          )}

          {organizationLevel === 'department' && (
            <LedgerField label="Department">
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <LedgerSelect
                    ariaLabel="Department"
                    value={field.value}
                    options={departmentOptions}
                    disabled={!teamId}
                    onValueChange={(next) => {
                      field.onChange(next);
                      refreshOrgMessage();
                    }}
                  />
                )}
              />
            </LedgerField>
          )}

          <LedgerField label="Risk Owner" error={errors.owner?.message}>
            <Controller
              name="owner"
              control={control}
              render={({ field }) => (
                <LedgerCombobox
                  ariaLabel="Risk Owner"
                  searchLabel="Search people"
                  value={field.value}
                  options={ownerOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Treatment Owner">
            <Controller
              name="treatment_owner"
              control={control}
              render={({ field }) => (
                <LedgerCombobox
                  ariaLabel="Treatment Owner"
                  searchLabel="Search people"
                  value={field.value}
                  options={treatmentOwnerOptions}
                  onValueChange={field.onChange}
                />
              )}
            />
          </LedgerField>

          <LedgerField
            label="Vendor note (optional)"
            htmlFor="risk-vendor-note"
            hint="Vendor or supplier this risk relates to"
          >
            <LedgerInput id="risk-vendor-note" list="risk-vendor-notes" {...register('vendor_note')} />
            <datalist id="risk-vendor-notes">
              {(vendorNotesQuery.data ?? []).map((note) => (
                <option key={note} value={note} />
              ))}
            </datalist>
          </LedgerField>

          <div className={sectionRuleClass}>Review Dates</div>

          <LedgerField label="Next Review Date" htmlFor="risk-next-review-date">
            <LedgerInput id="risk-next-review-date" type="date" {...register('next_review_date')} />
          </LedgerField>

          <LedgerField label="Last Reviewed Date" htmlFor="risk-last-reviewed-date">
            <LedgerInput id="risk-last-reviewed-date" type="date" {...register('last_reviewed_date')} />
          </LedgerField>

          <LedgerField label="Tags" htmlFor="risk-tags" hint="Press Enter to add tags" className="sm:col-span-2">
            <Controller
              name="tags"
              control={control}
              render={({ field }) => <TagsField value={field.value ?? []} onChange={field.onChange} />}
            />
          </LedgerField>
        </div>
      ),
    },
  ];

  const wizard = useLedgerWizard<RiskFormValues>({
    steps,
    mode: risk ? 'free' : 'linear',
    formId: 'risk-form',
    submitLabel: risk ? 'Save risk' : 'Create risk',
    isSubmitting: saving || isSubmitting,
    onCancel: handleClose,
  });
  const resetWizard = wizard.reset;

  // Reseed whenever the dialog opens or its subject changes, and rewind the
  // wizard to its first step (RHF's reset and the wizard's are different
  // resets — both must run here). Edit mode seeds from the row immediately,
  // then refetches the full record; the ignore flag keeps a slow, superseded
  // fetch from clobbering a newer seed (the reset-clobber bug that
  // risk-edit-fix.spec.ts guards).
  useEffect(() => {
    let ignore = false;
    setError(null);
    resetWizard();
    if (risk && open) {
      reset(valuesFromRisk(risk));
      riskService
        .getRisk(risk.id)
        .then((full) => {
          if (!ignore) reset(valuesFromRisk(full));
        })
        .catch(() => {
          if (!ignore) setError('Failed to load risk details. Please try again.');
        });
    } else {
      reset(defaultValues);
    }
    return () => {
      ignore = true;
    };
    // resetWizard is re-created each render but only rewinds internal step
    // state, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [risk, open, reset]);

  const onValid = async (values: RiskFormValues) => {
    setError(null);
    try {
      if (risk) {
        await updateRisk.mutateAsync({ id: risk.id, data: toRiskUpdatePayload(values, risk) });
      } else {
        await createRisk.mutateAsync(toRiskPayload(values));
      }
      onSuccess();
      handleClose();
    } catch (err) {
      const response = (
        err as { response?: { data?: { detail?: string; message?: string; non_field_errors?: string[] } } }
      ).response;
      setError(
        response?.data?.detail ||
          response?.data?.message ||
          response?.data?.non_field_errors?.[0] ||
          (err as Error).message ||
          'Failed to save risk',
      );
    }
  };

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title={risk ? 'Edit Risk' : 'Create New Risk'}
      size="md"
      preventClose={saving || isSubmitting}
      initialFocus={titleInputRef}
      footer={wizard.footer}
    >
      <form
        id="risk-form"
        noValidate
        onSubmit={handleSubmit(onValid, (submitErrors) => wizard.goToFirstInvalid(submitErrors))}
      >
        {(error || orgOptionsError) && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error ?? 'Failed to load organizations'}
          </p>
        )}
        {wizard.rail}
        {wizard.body}
      </form>
    </LedgerDialog>
  );
};

// The wizard hook reads RHF through useFormContext, so the form methods are
// owned here and provided down; the provider (and the dialog inside it) stay
// permanently mounted — LedgerDialog handles visibility.
export const RiskFormDialog: React.FC<RiskFormDialogProps> = (props) => {
  const methods = useForm<RiskFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onTouched',
  });
  return (
    <FormProvider {...methods}>
      <RiskFormDialogInner {...props} />
    </FormProvider>
  );
};
